/**
 * Constants and pure helpers used across the warranty class detail subcomponents.
 *
 * Kept separate from component files so fast-refresh keeps working.
 */

import type { FieldType } from '@/admin/types/warranties'

// ── Constants ───────────────────────────────────────────────

export const FIELD_TYPES: FieldType[] = [
  'TEXT', 'RICH_TEXT', 'NUMBER', 'BOOLEAN', 'SELECT', 'MULTI_SELECT',
  'DATE', 'DATE_TIME', 'DOCUMENT', 'FILE', 'IMAGE', 'VIDEO',
  'LINK', 'MONEY', 'ADDRESS', 'PHONE', 'EMAIL',
]

export const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  TEXT: 'Text',
  RICH_TEXT: 'Rich Text',
  NUMBER: 'Number',
  BOOLEAN: 'Boolean',
  SELECT: 'Select',
  MULTI_SELECT: 'Multi Select',
  DOUBLE_SELECT: 'Double Select',
  DATE: 'Date',
  DATE_TIME: 'Date/Time',
  DOCUMENT: 'Document',
  FILE: 'File',
  IMAGE: 'Image',
  VIDEO: 'Video',
  LINK: 'Link',
  MONEY: 'Money',
  ADDRESS: 'Address',
  PHONE: 'Phone',
  EMAIL: 'Email',
}

export const TYPE_LABELS: Record<string, string> = {
  MANUFACTURER_WARRANTY: 'Standard Warranty',
  EXTENDED_WARRANTY: 'Extended Warranty',
  RETAILER_WARRANTY: 'Retailer Warranty',
}

export const COVERAGE_LABELS: Record<string, string> = {
  MATERIAL_ONLY: 'Material Only',
  MATERIAL_AND_LABOR: 'Material & Labour',
}

// ── Pure helpers ────────────────────────────────────────────

export function isSelectType(ft: FieldType) {
  return ft === 'SELECT' || ft === 'MULTI_SELECT'
}

export function slugify(val: string) {
  return val
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+$/, '')
}

/** Normalise options from backend */
export function normaliseOptions(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  return raw.map((o) => {
    if (typeof o === 'string') return o
    if (o && typeof o === 'object' && 'label' in o) return String((o as { label: string }).label)
    if (o && typeof o === 'object' && 'value' in o) return String((o as { value: string }).value)
    return String(o)
  })
}

export function optionsToPayload(opts: string[]): Array<{ label: string; value: string }> {
  return opts.map((o) => ({ label: o, value: o }))
}
